// frontend/src/features/livestock/shareLinkActions.ts
// Copy and the system share sheet for a share link (design 8.6). Each runs
// from its own tap: iOS drops the user gesture across an await, so neither is
// ever started after the link is created in the same handler.

import { formatPhotoDay } from './photoInboxFormat';

/** The line over a shared page, as viewers see it (no name or account). */
export const SHARED_BY = 'Shared by a Reef Data Hub user';

/** Starts the clipboard write synchronously in the tap; false when the browser refuses. */
export async function copyLink(url: string): Promise<boolean> {
  try {
    if (!navigator.clipboard?.writeText) return false;
    await navigator.clipboard.writeText(url);
    return true;
  } catch {
    return false;
  }
}

export function canOpenShareSheet(): boolean {
  return typeof navigator !== 'undefined' && typeof navigator.share === 'function';
}

export type ShareSheetOutcome = 'shared' | 'cancelled' | 'failed';

/** navigator.share({title, url}); 'failed' means fall back to Copy. */
export async function openShareSheet(title: string, url: string): Promise<ShareSheetOutcome> {
  if (!canOpenShareSheet()) return 'failed';
  try {
    await navigator.share({ title, url });
    return 'shared';
  } catch (error) {
    // Dismissed by the person (a DOMException, not always instanceof Error).
    const name =
      typeof error === 'object' && error !== null ? (error as { name?: unknown }).name : null;
    return name === 'AbortError' ? 'cancelled' : 'failed';
  }
}

/** A link's dates as its owner sees them; "(photo removed)" for a deleted shot. */
export function linkDates(beforeDate: string | null, afterDate: string | null): string {
  const day = (value: string | null): string => (value ? formatPhotoDay(value) : '(photo removed)');
  return `${day(beforeDate)} to ${day(afterDate)}`;
}

/** A delete confirm's warning for a photo that public links show (tank shots, organism photos). */
export function sharedLinkWarning(count: number): string {
  return `Used in ${count} shared ${count === 1 ? 'link' : 'links'}, which will stop working.`;
}
